import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Skeleton, Empty } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import CustomPagination from '../../components/common/CustomPagination';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import httpClient from '../../utils/HttpClient';
import { objectToQuery, formatReadingTime } from '../../utils/Utils';
import { TECHNICAL_URL, TECHNICAL_CATEGORY_URL } from '../../constants/Url';
import { setTechnologyDetailListAction } from '../../modules/dataWebsite/actions';
import PROModal from '../../components/PROModal';
import AuthModal from '../../components/auth/AuthModal';
import { useMemberAccess } from '../../hooks/useMemberAccess';

export default function TechnologyDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPrimary = Boolean(location.state?.fromPrimary);
  const { categoryID } = useParams();
  const authModalRef = useRef();
  const user = useSelector((state) => state.authReducer);
  const { hasNoActiveMembership } = useMemberAccess();
  const [isMemberGateOpen, setIsMemberGateOpen] = useState(false);

  const handleNavigate = (path) => {
    if (!user?.token) {
      authModalRef.current?.open();
    } else if (!user?.user?.completedTS) {
      authModalRef.current?.open('REGISTER');
    } else if (hasNoActiveMembership()) {
      setIsMemberGateOpen(true);
    } else {
      navigate(path);
    }
  };

  // Connect mapped state to Redux
  const items = useSelector((state) => state.dataWebsiteReducer?.technologyDetailListData) || [];

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [categoryName, setCategoryName] = useState('');
  const [description, setDescription] = useState('');
  const [pageSize, setPageSize] = useState(9);

  useEffect(() => {
    setCurrentPage(1);
    setPageSize(9);
  }, [categoryID]);

  const fetchCategoryName = async () => {
    const staticCategories = {
      '技术展示': { name: 'menu_tech_showcase', description: 'menu_tech_showcase_sub' },
      '冷知识': { name: 'menu_trivia', description: 'menu_trivia_sub' },
    };

    // 1. Instantly set from static data if available (URL param is often the name)
    if (staticCategories[categoryID]) {
      setCategoryName(staticCategories[categoryID].name);
      setDescription(staticCategories[categoryID].description);
    } else {
      setCategoryName(categoryID); // Fallback to just the param
    }

    try {
      const res = await httpClient.get(objectToQuery(TECHNICAL_CATEGORY_URL))
        .then(res => res.data)
        .catch(error => ({ status: 500, message: error?.message }));

      if (res.status === 200) {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.list || []);
        const found = list.find(c => String(c._id || c.id) === String(categoryID) || String(c.name) === String(categoryID));
        if (found) {
          setCategoryName(found?.name);
          // Prioritize API description, fallback to static if API description is empty
          setDescription(found?.description || (staticCategories[found?.name]?.description || ''));
        }
      }
    } catch (error) {
      console.error('fetchCategoryName -> error', error);
    }
  };

  const fetchTechnicals = async () => {
    try {
      setLoading(true);
      const payload = {
        keyword: '',
        category: categoryID || '',
        // categoryId: categoryID || '',
        releaseYear: 'ALL',
        pageNo: currentPage,
        pageSize: pageSize
      };
      const res = await httpClient.get(objectToQuery(TECHNICAL_URL, payload))
        .then(res => res.data)
        .catch(error => ({ status: 500, message: error?.message }));

      if (res.status === 200) {
        setTechnologyDetailListAction(res.data || []);
        setTotalItems(res.total || res.data?.length || 0);
      } else {
        setTechnologyDetailListAction([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error('fetchTechnicals -> error', error);
      setTechnologyDetailListAction([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategoryName();
    fetchTechnicals();
  }, [categoryID, categoryName, description, currentPage, pageSize]);



  return (
    <div className="w-full min-h-screen pb-16 bg-[#f7f9fc]">

      {/* ── Top Header Banner Section ── */}
      <div className="w-full py-10 px-4 text-center bg-gradient-to-r from-[#4d9dec] via-[#637cfb] to-[#a380f9] text-white">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide m-0 text-white mb-2">
          {t(categoryName || "")}
        </h1>
        <p className="text-[13px] sm:text-[14px] opacity-90 m-0">
          {t(description || "")}
        </p>
      </div>

      <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        {fromPrimary && (
          <div className="flex justify-between items-center mb-6">
            <div 
              onClick={() => {
                navigate('/education');
              }} 
              className="inline-flex items-center gap-2 cursor-pointer text-gray-500 hover:text-black transition-colors font-medium text-sm sm:text-base"
            >
              <ArrowLeftOutlined className="text-base" />
              <span>{t('text_back_previous_page')}</span>
            </div>
          </div>
        )}

        {/* 3-Column Exhibition Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-100 p-5 sm:p-6 flex flex-col gap-4">
                <div className="w-full aspect-[4/3] rounded-md bg-gray-50 flex items-center justify-center overflow-hidden">
                  <Skeleton.Image className="!w-full !h-full" active />
                </div>
                <Skeleton.Input active size="default" style={{ width: '80%' }} />
                <Skeleton active paragraph={{ rows: 2 }} title={false} className="mt-2" />
                <div className="flex justify-between items-center pt-4 border-t border-gray-50 mt-auto">
                  <Skeleton.Input active size="small" style={{ width: '30%', height: '16px' }} />
                  <Skeleton.Input active size="small" style={{ width: '25%', height: '16px' }} />
                </div>
              </div>
            ))}
          </div>
        ) : items.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {items.map((item, index) => (
              <div
                key={(item._id || item.id) + '-' + index}
                className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full"
              >
                {/* Image cover */}
                <div className='' onClick={() => handleNavigate(`/technical/${item.technicalsId}`)}>
                  <div className="w-full aspect-[4/3] overflow-hidden relative">
                    <img
                      src={item.coverImage || item.coverimage || item.image || item.imageUrl || item.imagePath}
                      alt={item.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>

                  {/* Content block */}
                  <div className="p-5 sm:p-6 flex flex-col flex-1">
                    <h3
                      className="text-[15px] sm:text-base font-bold text-gray-900 leading-snug mb-3 line-clamp-1 cursor-pointer hover:text-[#637cfb] transition-colors"
                    >
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-xs sm:text-[13px] leading-relaxed line-clamp-3 mb-6 flex-1">
                      {item?.subtitle}
                    </p>

                    {/* Footer details */}
                    <div className="flex justify-between items-center pt-4 border-t border-gray-50 mt-auto">
                      <span className="text-[12px] text-gray-400">
                        {t('text_read_time')}: {formatReadingTime(item?.readingTime)}
                      </span>
                      <span
                        onClick={() => handleNavigate(`/technical/${item?.technicalsId}`)}
                        className="text-[12px] gradient-text-button font-bold flex items-center gap-1 cursor-pointer transition-colors"
                      >
                        {t('text_in_depth')} &rarr;
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('text_no_content')} />
        )}

        {/* Pagination */}
        {(loading || totalItems > 0 || items.length > 0) && (
          <div className="flex justify-end mt-12">
            <CustomPagination
              current={currentPage}
              pageSize={pageSize}
              total={loading ? 9 : (totalItems || items.length || 0)}
              pageSizeOptions={[9, 18, 27, 36, 45]}
              onChange={(page, limit) => {
                if (limit !== pageSize) {
                  setPageSize(limit);
                  setCurrentPage(1);
                } else {
                  setCurrentPage(page);
                }
              }}
            />
          </div>
        )}
      </div>
      <AuthModal ref={authModalRef} />
      <PROModal open={isMemberGateOpen} onCancel={() => setIsMemberGateOpen(false)} />
    </div>
  );
}
